'use client';

import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
// import { OrbitControls } from 'three/examples/jsm/Addons.js';
// import { GLTFLoader } from 'three/examples/jsm/Addons.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

const Hero3DModel = () => {
	const mountRef = useRef(null);

	useEffect(() => {
		if (!mountRef.current) return;

		// Scene setup
		const scene = new THREE.Scene();
		const camera = new THREE.PerspectiveCamera(
			75,
			mountRef.current.clientWidth / mountRef.current.clientHeight,
			0.1,
			1000
		);
		const renderer = new THREE.WebGLRenderer({ 
			antialias: true, 
			alpha: true,
			powerPreference: "high-performance",
			precision: "highp"
		});

		renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
		renderer.setPixelRatio(window.devicePixelRatio); // Match device pixel ratio
		renderer.domElement.style.position = 'absolute';
		renderer.domElement.style.zIndex = '1';
		renderer.outputColorSpace = THREE.SRGBColorSpace; // Modern color space handling
		renderer.toneMapping = THREE.ACESFilmicToneMapping;
		renderer.toneMappingExposure = 1.2; // Slightly increased exposure
		renderer.shadowMap.enabled = true;
		renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		mountRef.current.appendChild(renderer.domElement);

		// Lighting
		const ambientLight = new THREE.AmbientLight(0xffffff, 1); // Increased intensity
		scene.add(ambientLight);

		const directionalLight = new THREE.DirectionalLight(0xffffff, 2); // Increased intensity
		directionalLight.position.set(5, 5, 5);
		directionalLight.castShadow = true;
		directionalLight.shadow.mapSize.width = 1024;
		directionalLight.shadow.mapSize.height = 1024;
		scene.add(directionalLight);

		// Add fill light from the opposite side
		const fillLight = new THREE.DirectionalLight(0xffffff, 1);
		fillLight.position.set(-5, 0, -5);
		scene.add(fillLight);

		// Load 3D model
		const loader = new GLTFLoader();
		let mixer = null;

		loader.load(
			'/assets/683e86c6c4613c9578dc1cd1.glb',
			(gltf) => {
				const model = gltf.scene;

				// Adjust model scale and position
				model.scale.set(3, 3, 3); // Increased scale
				model.position.set(0, -2, 0); // Adjusted position to compensate for larger scale

				// Setup animations
				if (gltf.animations.length > 0) {
					mixer = new THREE.AnimationMixer(model);
					const action = mixer.clipAction(gltf.animations[0]);
					action.play();
				}

				// Traverse the model to adjust materials if needed
				model.traverse((child) => {
					if (child instanceof THREE.Mesh) {
						if (child.material) {
							child.material.needsUpdate = true;
							child.material.side = THREE.DoubleSide;
							
							// Enhance material quality
							if (child.material.map) {
								child.material.map.anisotropy = renderer.capabilities.getMaxAnisotropy();
							}
							
							// Improve shadow and light interaction
							child.castShadow = true;
							child.receiveShadow = true;
						}
					}
				});

				scene.add(model);

				// Adjust camera to fit model
				const box = new THREE.Box3().setFromObject(model);
				const center = box.getCenter(new THREE.Vector3());
				const size = box.getSize(new THREE.Vector3());

				const maxDim = Math.max(size.x, size.y, size.z);
				const fov = camera.fov * (Math.PI / 180);
				let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

				cameraZ *= 0.5; // Zoom out a little so object fits in view

				camera.position.set(center.x, center.y, center.z + cameraZ);
				camera.lookAt(center);

				// Adjust far plane of camera
				camera.far = cameraZ * 3;
				camera.updateProjectionMatrix();

				// Update orbit controls target
				controls.target.set(center.x, center.y, center.z);
				controls.update();
			},
			undefined,
			(error) => {
				console.error('An error occurred while loading the 3D model:', error);
			}
		);

		// Camera position
		camera.position.z = 1;

		// Orbit controls
		const controls = new OrbitControls(camera, renderer.domElement);
		controls.enableDamping = true;
		controls.dampingFactor = 0.05;
		controls.screenSpacePanning = false;
		controls.minDistance = 5;
		controls.maxDistance = 10;
		// Restrict vertical rotation
		controls.minPolarAngle = Math.PI / 2; // Lock to horizontal plane
		controls.maxPolarAngle = Math.PI / 2; // Lock to horizontal plane
		controls.enableZoom = false; // Disable zooming

		// Animation loop
		const clock = new THREE.Clock();
		const animate = () => {
			requestAnimationFrame(animate);

			const delta = clock.getDelta();
			if (mixer) mixer.update(delta);

			controls.update();
			renderer.render(scene, camera);
		};
		animate();

		// Handle window resize
		const handleResize = () => {
			if (!mountRef.current) return;
			const width = mountRef.current.clientWidth;
			const height = mountRef.current.clientHeight;
			camera.aspect = width / height;
			camera.updateProjectionMatrix();
			renderer.setSize(width, height);
		};
		window.addEventListener('resize', handleResize);

		// Cleanup
		return () => {
			window.removeEventListener('resize', handleResize);
			mountRef.current?.removeChild(renderer.domElement);
		};
	}, []);

	return (
		
			<div ref={mountRef} className='w-full overflow-visible h-[400px] relative z-10' style={{ transform: 'scale(1.5)' }} />

	);
};

export default Hero3DModel;
